import { useMemo } from 'react'
import { AlertTriangle, Layers, MapPinOff, Radio } from 'lucide-react'
import type { BoundingBox, LatLon } from '../../lib/geo'
import { isValidCoord } from '../../lib/geo'
import { formatDecimal } from '../../lib/geo'
import { LAYER_COLOR } from './layers'

/* ============================================================================
 * ЗАГЛУШКА КАРТЫ — КООРДИНАТНАЯ СЕТКА
 *
 * Используется, когда стиль или тайлы MapLibre недостижимы (офлайн-режим).
 * Требование ТЗ: НЕ показывать пустой серый блок. Вместо этого рисуем
 * координатную сетку WGS-84 с подписями и наносим цели, маршруты и районы
 * теми же условными обозначениями, что и на живой карте.
 * ========================================================================= */

interface GridPoint {
  id: string
  lat: number
  lon: number
  label: string
  kind: 'cooperative' | 'noncooperative' | 'intruder' | 'ownship' | 'vessel'
}

interface GridLine {
  id: string
  points: Array<[number, number]>
  color: string
  dashed?: boolean
}

interface GridPolygon {
  id: string
  /**
   * Тип полигона — определяет, в какой источник MapLibre он попадёт:
   * 'geofence' -> SRC.geofences (объёмная зона), 'mission' -> SRC.missions.
   * Без этого признака зоны и районы сливались в один источник.
   */
  kind?: 'geofence' | 'mission'
  points: Array<[number, number]>
  color: string
  label: string
  /**
   * Полоса высот, м над MSL (задача №14). Нужна, чтобы в 3D-режиме полигон
   * превращался в объём, а в сетке — подписывался «0–120 м».
   * Отсутствует = зона без заданной высоты, показываем как есть.
   */
  lowerLimit?: number
  upperLimit?: number
  /** Опорная высота: 0=AMSL, 1=AGL (InterUSS) — рендер объёма зависит от этого */
  altitudeRef?: number
  /** Рельеф под зоной (бэкенд по DEM), м ASL; null = недоступен */
  groundMin?: number | null
  groundMax?: number | null
  /** Источник зоны (п.2, 2026-10-06): mintc | sppi | own — для фильтра на карте */
  source?: 'mintc' | 'sppi' | 'own'
}

/** Выбор «красивого» шага сетки, дающего 4–12 линий по каждой оси */
function niceStep(span: number): number {
  const target = span / 8
  const candidates = [
    0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10,
  ]
  for (const c of candidates) if (c >= target) return c
  return candidates[candidates.length - 1]
}

/** Проекция координат в пиксели области просмотра (равномерная, без Меркатора —
 *  для заглушки достаточно, подписи сетки остаются читаемыми) */
function makeProjector(box: BoundingBox, width: number, height: number) {
  const lonSpan = Math.max(1e-6, box.lon2 - box.lon1)
  const latSpan = Math.max(1e-6, box.lat2 - box.lat1)
  // Подгоняем под соотношение сторон, чтобы сетка была квадратной
  const aspect = width / Math.max(1, height)
  let spanLon = lonSpan
  let spanLat = latSpan
  if (spanLon / spanLat > aspect) {
    spanLat = spanLon / aspect
  } else {
    spanLon = spanLat * aspect
  }
  const centerLon = (box.lon1 + box.lon2) / 2
  const centerLat = (box.lat1 + box.lat2) / 2

  const x = (lon: number): number => ((lon - (centerLon - spanLon / 2)) / spanLon) * width
  const y = (lat: number): number => height - ((lat - (centerLat - spanLat / 2)) / spanLat) * height
  return { x, y }
}

/**
 * Компонент-заглушка карты.
 *
 * @param box видимая область
 * @param points цели для нанесения
 * @param lines маршруты и линии (декларации, РЛС-векторы)
 * @param polygons районы и геозоны
 * @param reason причина деградации для показа оператору
 */
export function CoordinateGridFallback({
  box,
  points,
  lines = [],
  polygons = [],
  reason,
  width = 960,
  height = 620,
}: {
  box: BoundingBox
  points: GridPoint[]
  lines?: GridLine[]
  polygons?: GridPolygon[]
  reason?: string
  width?: number
  height?: number
}) {
  const project = useMemo(() => makeProjector(box, width, height), [box, width, height])

  // --- Линии сетки с подписями ---
  const gridLines = useMemo(() => {
    const stepLat = niceStep(Math.max(1e-6, box.lat2 - box.lat1))
    const stepLon = niceStep(Math.max(1e-6, box.lon2 - box.lon1))
    const verticals: Array<{ lon: number; major: boolean }> = []
    const horizontals: Array<{ lat: number; major: boolean }> = []

    const firstLon = Math.ceil(box.lon1 / stepLon) * stepLon
    for (let lon = firstLon; lon <= box.lon2 && verticals.length < 40; lon += stepLon) {
      verticals.push({ lon, major: Math.abs(lon % (stepLon * 5)) < 1e-9 })
    }
    const firstLat = Math.ceil(box.lat1 / stepLat) * stepLat
    for (let lat = firstLat; lat <= box.lat2 && horizontals.length < 40; lat += stepLat) {
      horizontals.push({ lat, major: Math.abs(lat % (stepLat * 5)) < 1e-9 })
    }
    return { verticals, horizontals, stepLat, stepLon }
  }, [box])

  const ownship = points.find((p) => p.kind === 'ownship')

  return (
    <div className="ut-grid-fallback" data-fallback="coordinate-grid">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid slice" role="img"
           aria-label="Координатная сетка WGS-84: карта недоступна">
        <defs>
          <pattern id="ut-hatch" width="8" height="8" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
            <line x1="0" y1="0" x2="0" y2="8" stroke={LAYER_COLOR.geofences} strokeWidth="1" opacity="0.18" />
          </pattern>
        </defs>

        {/* Районы и геозоны */}
        {polygons.map((poly) => (
          <g key={poly.id}>
            <polygon
              points={poly.points.map(([lon, lat]) => `${project.x(lon)},${project.y(lat)}`).join(' ')}
              fill={poly.color}
              fillOpacity={0.12}
              stroke={poly.color}
              strokeWidth={2}
              strokeDasharray="4 3"
            />
            <text
              x={project.x(poly.points[0][0]) + 6}
              y={project.y(poly.points[0][1]) - 6}
              className="ut-grid-label"
              fill={poly.color}
            >
              {poly.label}
            </text>
          </g>
        ))}

        {/* Вертикали сетки */}
        {gridLines.verticals.map((v) => (
          <g key={`v${v.lon.toFixed(6)}`}>
            <line
              x1={project.x(v.lon)} y1={0} x2={project.x(v.lon)} y2={height}
              className={v.major ? 'ut-grid-line-major' : 'ut-grid-line'}
            />
            <text x={project.x(v.lon) + 3} y={height - 6} className="ut-grid-label">
              {v.lon.toFixed(4)}
            </text>
          </g>
        ))}

        {/* Горизонтали сетки */}
        {gridLines.horizontals.map((h) => (
          <g key={`h${h.lat.toFixed(6)}`}>
            <line
              x1={0} y1={project.y(h.lat)} x2={width} y2={project.y(h.lat)}
              className={h.major ? 'ut-grid-line-major' : 'ut-grid-line'}
            />
            <text x={4} y={project.y(h.lat) - 3} className="ut-grid-label">
              {h.lat.toFixed(4)}
            </text>
          </g>
        ))}

        {/* Маршруты и линии */}
        {lines.map((l) => (
          <polyline
            key={l.id}
            points={l.points.map(([lon, lat]) => `${project.x(lon)},${project.y(lat)}`).join(' ')}
            className="ut-fallback-track"
            style={{ color: l.color }}
            strokeDasharray={l.dashed ? '4 3' : undefined}
          />
        ))}

        {/* Вектор «свой БВС → цель» для инструмента Дальность и азимут */}
        {ownship && points.filter((p) => p.kind === 'intruder').map((p) => (
          <line
            key={`rb-${ownship.id}-${p.id}`}
            x1={project.x(ownship.lon)} y1={project.y(ownship.lat)}
            x2={project.x(p.lon)} y2={project.y(p.lat)}
            className="ut-range-bearing"
          />
        ))}

        {/* Цели */}
        {points.map((p) => {
          if (!isValidCoord(p.lat, p.lon)) return null
          const color =
            p.kind === 'intruder'
              ? LAYER_COLOR.intruders
              : p.kind === 'noncooperative'
                ? LAYER_COLOR.nonCooperative
                : p.kind === 'cooperative'
                  ? LAYER_COLOR.cooperative
                  : p.kind === 'ownship'
                    ? LAYER_COLOR.declarations
                    : LAYER_COLOR.vessels
          const cx = project.x(p.lon)
          const cy = project.y(p.lat)
          return (
            <g key={p.id} style={{ color }}>
              {p.kind === 'intruder' && (
                <circle cx={cx} cy={cy} r={9} className="ut-fallback-halo" style={{ color }} />
              )}
              <circle cx={cx} cy={cy} r={5} className="ut-fallback-point" style={{ color }} />
              {p.kind === 'ownship' && (
                <circle cx={cx} cy={cy} r={9} className="ut-grid-crosshair" style={{ color }} />
              )}
              <text x={cx + 8} y={cy - 6} className="ut-grid-label" fill={color}>
                {p.label}
              </text>
            </g>
          )
        })}

        {/* Перекрестье центра вида */}
        <line x1={width / 2} y1={0} x2={width / 2} y2={height} className="ut-grid-crosshair" />
        <line x1={0} y1={height / 2} x2={width} y2={height / 2} className="ut-grid-crosshair" />
      </svg>

      {/* Пояснение оператору: почему карта в режиме сетки */}
      <div className="ut-fade-up absolute left-3 top-3 flex max-w-sm items-start gap-2 rounded-md border border-line bg-surface/95 px-2.5 py-2 shadow-panel">
        <MapPinOff size={14} className="mt-0.5 shrink-0 text-warning" aria-hidden />
        <div className="text-[11px] leading-snug">
          <div className="font-semibold text-ink">Карта недоступна — режим координатной сетки</div>
          <div className="text-ink-muted">
            Стиль/тайлы не загружены. Показаны координаты WGS-84 и цели по последним данным.
            {reason ? ` Причина: ${reason}.` : ''}
          </div>
        </div>
      </div>

      {/* Условные обозначения */}
      <div className="absolute bottom-3 left-3 flex flex-wrap gap-x-3 gap-y-1 rounded-md border border-line bg-surface/95 px-2.5 py-1.5 text-[10px] shadow-panel">
        <LegendItem color={LAYER_COLOR.cooperative} label="Кооперативная (ADS-B / Remote ID)" dashed={false} />
        <LegendItem color={LAYER_COLOR.nonCooperative} label="Некооперативная (РЛС / RF)" dashed />
        <LegendItem color={LAYER_COLOR.intruders} label="Нарушитель" dashed={false} pulse />
        <LegendItem color={LAYER_COLOR.missions} label="Район задания" dashed={false} />
        <LegendItem color={LAYER_COLOR.geofences} label="Геозона" dashed />
      </div>

      {/* Координаты центра вида */}
      <div className="absolute bottom-3 right-3 rounded-md border border-line bg-surface/95 px-2 py-1 shadow-panel">
        <div className="flex items-center gap-1.5">
          <Radio size={10} className="text-ink-muted" aria-hidden />
          <span className="ut-metric text-[10px] text-ink-muted">
            {formatDecimal((box.lat1 + box.lat2) / 2, (box.lon1 + box.lon2) / 2, 4)}
          </span>
        </div>
      </div>
    </div>
  )
}

/** Элемент условных обозначений */
function LegendItem({
  color,
  label,
  dashed,
  pulse = false,
}: {
  color: string
  label: string
  dashed: boolean
  pulse?: boolean
}) {
  return (
    <span className="flex items-center gap-1.5 text-ink-muted">
      <span
        className={`inline-block h-2.5 w-2.5 rounded-full border-2 ${pulse ? 'ut-pulsing' : ''}`}
        style={{
          borderColor: color,
          background: 'transparent',
          borderStyle: dashed ? 'dashed' : 'solid',
        }}
        aria-hidden
      />
      {label}
    </span>
  )
}

/** Тип для внешнего использования */
export type { GridPoint, GridLine, GridPolygon }

/** Пустой массив целей для заглушки, когда данных нет */
export const NO_POINTS: GridPoint[] = []

/** Список слоёв для отображения в UI (используется легендой и переключателями) */
export const MAP_LAYER_META = [
  { id: 'missions', label: 'Районы заданий', color: LAYER_COLOR.missions },
  { id: 'geofences', label: 'Геозоны', color: LAYER_COLOR.geofences },
  { id: 'declarations', label: 'Декларации', color: LAYER_COLOR.declarations },
  { id: 'cooperativeTracks', label: 'Кооперативные цели', color: LAYER_COLOR.cooperative },
  { id: 'nonCooperativeTracks', label: 'Некооперативные цели', color: LAYER_COLOR.nonCooperative },
  { id: 'intruders', label: 'Нарушители', color: LAYER_COLOR.intruders },
  // Подсветка конфликтных дуг прогноза (2026-10-07f): цвет слоя predict,
  // красящего участников предсказанной пары (miss < 500 м на горизонте).
  { id: 'predictConflict', label: 'Прогноз конфликта', color: LAYER_COLOR.predictConflict },
] as const

/** Иконка слоя для легенды */
export const LayerIcon = Layers

/** Предупреждение при пустых данных в офлайне */
export function OfflineWarning({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] text-warning">
      <AlertTriangle size={12} aria-hidden />
      {text}
    </div>
  )
}

/** Тип точки области, удобный для вычислений вне компонента */
export type { LatLon }
